import { VITE_PUBLIC_ENV, VITE_SENTRY_DSN } from "./utils/env";
import * as Sentry from "@sentry/react";

// check if the environment is live (production or preview)
const isLiveEnv = VITE_PUBLIC_ENV && (VITE_PUBLIC_ENV === "production" || VITE_PUBLIC_ENV === "preview") || false;

const errorCooldowns = new Map(); // Store user error timestamps
// ignore these errors from being sent to Sentry
const ignoreReplayErrors = [
    "Object captured as exception with keys: code, details, hint, message"
]

/**
 * Determines whether an error message should be sent based on the user ID and error message.
 * Implements a cool-down mechanism to prevent sending the same error message for the same user
 * within a 10-minute window.
 *
 * @param userId - The ID of the user. Can be a string, number, or undefined.
 * @param errorMessage - The error message to be sent. Can be a string or undefined.
 * @returns `true` if the error message should be sent, `false` otherwise.
 */
function shouldSendError(userId: string | number | undefined, errorMessage: string | undefined) {
    // if either the user ID or error message is missing, don't send the error
    if (!userId || !errorMessage) {
        return false;
    }

    const key = `${userId}:${errorMessage}`;
    const lastErrorTime = errorCooldowns.get(key);
    const now = Date.now();

    // error happened in the last 10 minutes
    if (lastErrorTime && now - lastErrorTime < 600000) { // 10 minutes
        return false;
    }

    errorCooldowns.set(key, now);
    return true;
}
function initSentry() {
    if (!VITE_SENTRY_DSN) {
        return;
    }

    try {
        Sentry.init({
            dsn: VITE_SENTRY_DSN,
            // replaysSessionSampleRate: isLiveEnv ? sentrySessionSampleRate : 0,
            // replaysOnErrorSampleRate: isLiveEnv ? 1.0 : 0, // 100% of sessions
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 0,

            // Because of an issue with MUI (possibly introduced with v7), DataGrids are triggering an error
            // that the height is zero when being used in Headless UI Dialogs. This appears to be because
            // the height is set to zero when the Dialog is closed, but when when the dialog is opened, the
            // error is triggered before it can recalculate the height. The only place where this is
            // happening is the IncompleteModal. If it fixes itself, we can remove this.
            ignoreErrors: [
                "useResizeContainer - The parent DOM element of the Data Grid has an empty height.",
            ],
            //debug: true,
            integrations: [
                // Sentry.reactRouterV6BrowserTracingIntegration({
                // 	useEffect: React.useEffect,
                // 	useLocation,
                // 	useNavigationType,
                // 	createRoutesFromChildren,
                // 	matchRoutes,
                // }),
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    beforeErrorSampling: (error) => {
                        // if the error message is in the ignoreReplayErrors array, return false (don't send the replay)
                        if (error.message && ignoreReplayErrors.includes(error?.message)) {
                            return false;
                        }
                        return true;
                    }
                }),
                Sentry.captureConsoleIntegration(),
            ],
            environment: VITE_PUBLIC_ENV,

            // Set tracesSampleRate to 1.0 to capture 100%
            // of transactions for performance monitoring.
            tracesSampleRate: 1.0,

            // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
            tracePropagationTargets:
                isLiveEnv
                    ? [
                        /^https:\/\/service.gohappydoc.com\/api-server/,
                        /^https:\/\/service.gohappydoc.com\/service-transcribe/,
                        /^https:\/\/kezqvoudwepwufpjcjkk.supabase.co/,
                        /^https:\/\/supa.happydoc.ai/,
                    ]
                    : [
                        "localhost",
                        /^https:\/\/test.insights.gohappydoc.com\/api-server/,
                        /^https:\/\/test.insights.gohappydoc.com\/service-transcribe/,
                        /^https:\/\/ntelvrlfzbgggnlgnzup.supabase.co/,
                    ],
            beforeSend: (event, hint) => {
                // log to console if env === development
                if (!isLiveEnv) {
                    console.error(hint.originalException);
                    return null
                    // console.error(hint.originalException, event.extra); // if you want the extra stuff too
                }

                // filter out spammy errors
                if (event.user && event.exception) {
                    const userId = event.user.id;
                    const errorMessage = event?.exception?.values && event.exception.values[0]?.value;

                    if (!shouldSendError(userId, errorMessage)) {
                        return null; // Prevent sending duplicate errors
                    }
                }

                return event;
            },
            beforeBreadcrumb: (breadcrumb) => {
                // log to console if env === development
                if (process.env.VITE_PUBLIC_ENV === "development") {
                    console.log(breadcrumb);
                }

                return breadcrumb;
            },
            enabled: isLiveEnv,
        });
    } catch (e) {
        console.error("Error initializing Sentry", e);
    }
}

export default initSentry;
